import axios, { AxiosError, AxiosResponse } from 'axios';
import { all, call, put, takeLatest, takeLeading } from 'redux-saga/effects';
import * as API from '@api/types';
import * as AuthActions from './actions';
import { AuthAction } from './actionTypes';

const MAX_REFRESH_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 1000;
const SESSION_TOKEN_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours (Loop sessionToken validity)

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Sends a login link to the specified email address. The user can use this link to log in without a password.
 *
 * @param {string} email The email address to which the login link should be sent.
 * @returns {Promise<API.Response<API.AuthSendLoginLinkResponse>>} A promise that resolves to an empty success response upon successful delivery of the login link.
 */
const authSendLoginLink = async (
  email: string,
): Promise<API.Response<API.AuthSendLoginLinkResponse>> => {
  const params: API.AuthSendLoginLinkRequest = {
    email,
  };
  return await axios.post<API.AuthSendLoginLinkResponse>(`/api/v1/auth/link`, params);
};

/**
 * Authenticates a user using a session token, typically after the user has clicked a login link.
 *
 * @param {string} sessionToken The session token of the user obtained from the login link.
 * @returns {Promise<API.Response<API.AuthInitResponse>>} A promise that resolves to an object containing the user's access token if authentication is successful.
 */
const authInit = async (sessionToken: string): Promise<API.Response<API.AuthInitResponse>> => {
  const params: API.AuthInitRequest = {
    sessionToken,
  };

  return await axios.post(`/api/v1/auth`, params);
};

/**
 * Refreshes the user's access token using a valid refresh token. This is used to keep the user logged in without requiring reauthentication.
 *
 * @param {string} accessToken The current access token of the user that needs to be refreshed.
 * @returns {Promise<API.Response<API.AuthRefreshResponse>>} A promise that resolves to an object containing the new access token.
 */
const authRefresh = async (accessToken: string): Promise<API.Response<API.AuthRefreshResponse>> => {
  const params: API.AuthRefreshRequest = {
    accessToken,
  };

  return await axios.post(`/api/v1/auth/refresh`, params);
};

/**
 * Logs out the user by invalidating their access token. This ends the user's session.
 *
 * @param {string} accessToken The access token of the user to be logged out.
 * @returns {Promise<API.Response<API.AuthLogoutResponse>>} A promise that resolves to an empty success response upon successful logout.
 */
const authLogout = async (accessToken: string): Promise<API.Response<API.AuthLogoutResponse>> => {
  const params: API.AuthLogoutRequest = {
    accessToken,
  };

  return await axios.post(`/api/v1/logout`, params);
};

function* authRequestLoginLinkSaga(action: ReturnType<typeof AuthActions.authRequestLoginLink>) {
  try {
    const response: AxiosResponse<API.AuthSendLoginLinkResponse> = yield call(() =>
      authSendLoginLink(action.payload.email),
    );

    yield put(AuthActions.authRequestLoginLinkSuccess(response.data));
  } catch (e: unknown) {
    const axiosErr = e as AxiosError<{ code?: string; message?: string }>;
    const errorCode = axiosErr.response?.data?.code || '';

    yield put(
      AuthActions.authRequestLoginLinkFailure({
        error: {
          error: e instanceof Error ? e.message : 'Unknown error',
          code: errorCode,
        } as API.Error,
      }),
    );
  }
}

function* initAuthSaga(action: ReturnType<typeof AuthActions.authInit>) {
  try {
    const response: AxiosResponse<API.AuthInitResponse> = yield call(() =>
      authInit(action.payload.sessionToken),
    );

    yield put(AuthActions.authInitSuccess(response.data));
  } catch (e: unknown) {
    // Clear the expired/invalid sessionToken so the app doesn't infinitely retry
    localStorage.removeItem('auth.sessionToken');
    localStorage.removeItem('auth.sessionTokenTimestamp');

    yield put(
      AuthActions.authInitFailure({
        error: { error: e instanceof Error ? e.message : 'Unknown error' },
      }),
    );
  }
}

/**
 * Attempts to re-authenticate using the stored sessionToken from localStorage.
 * This is the fallback when access token refresh fails.
 *
 * @returns {boolean} true if re-auth succeeded, false otherwise
 */
function* attemptSessionTokenReauth(): Generator<any, boolean, any> {
  const storedSessionToken = localStorage.getItem('auth.sessionToken');
  const storedTimestamp = localStorage.getItem('auth.sessionTokenTimestamp');

  if (!storedSessionToken) {
    console.warn('[Auth] No sessionToken in localStorage — cannot fall back');
    return false;
  }

  if (storedTimestamp) {
    const age = Date.now() - Number(storedTimestamp);
    if (age > SESSION_TOKEN_MAX_AGE_MS) {
      console.warn(`[Auth] SessionToken too old (${Math.round(age / 3600000)}h) — clearing`);
      localStorage.removeItem('auth.sessionToken');
      localStorage.removeItem('auth.sessionTokenTimestamp');
      return false;
    }
  }

  // Note: Loop sessionTokens are single-use. If this token was already
  // exchanged during the initial login, this call will fail. This fallback
  // only succeeds when the user has an unconsumed token (e.g. from a fresh
  // magic link that was stored but never exchanged).
  try {
    console.info('[Auth] Attempting sessionToken re-auth fallback…');
    const response: AxiosResponse<API.AuthInitResponse> = yield call(() =>
      authInit(storedSessionToken),
    );
    console.info('[Auth] SessionToken re-auth succeeded');
    yield put(AuthActions.authRefreshSuccess(response.data));
    return true;
  } catch (e: unknown) {
    const axiosErr = e as AxiosError;
    console.warn(
      '[Auth] SessionToken re-auth failed:',
      e instanceof Error ? e.message : e,
      '| status=',
      axiosErr.response?.status,
    );
    return false;
  }
}

function* refreshAuthSaga(action: ReturnType<typeof AuthActions.authRefresh>) {
  for (let attempt = 1; attempt <= MAX_REFRESH_RETRIES; attempt++) {
    try {
      const response: AxiosResponse<API.AuthRefreshResponse> = yield call(() =>
        authRefresh(action.payload.accessToken),
      );
      yield put(AuthActions.authRefreshSuccess(response.data));
      return;
    } catch (e: unknown) {
      const axiosErr = e as AxiosError<{ message?: string; error?: string }>;
      const status = axiosErr.response?.status;
      const body = axiosErr.response?.data;

      console.warn(
        `[Auth] Refresh attempt ${attempt}/${MAX_REFRESH_RETRIES} failed:`,
        e instanceof Error ? e.message : e,
        `| status=${status}`,
        `| body=`,
        body,
      );

      // 400 with a message from the backend means the refresh token is
      // permanently invalid (stale, missing, or Loop rejected it).
      // Retrying with the same token won't help — break out early.
      if (status === 400) {
        console.warn('[Auth] Refresh returned 400 — skipping remaining retries');
        break;
      }

      if (attempt < MAX_REFRESH_RETRIES) {
        yield call(wait, RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1));
      }
    }
  }

  // All refresh retries exhausted — try sessionToken fallback
  const reauthed: boolean = yield call(attemptSessionTokenReauth);

  if (!reauthed) {
    console.warn('[Auth] All recovery exhausted (refresh + sessionToken) — forcing logout');
    localStorage.removeItem('auth.sessionToken');
    localStorage.removeItem('auth.sessionTokenTimestamp');
    // Wipe the entire Redux store (same as explicit logout) so the user
    // is redirected to the login page instead of sitting in a dead state.
    yield put(AuthActions.authLogoutSuccess({} as API.AuthLogoutResponse));
  }
}

function* logoutSaga(action: ReturnType<typeof AuthActions.authLogout>) {
  try {
    const response: AxiosResponse<API.AuthLogoutResponse> = yield call(() =>
      authLogout(action.payload.accessToken),
    );

    // Clear stored sessionToken so the user isn't auto-reauthenticated after logout
    localStorage.removeItem('auth.sessionToken');
    localStorage.removeItem('auth.sessionTokenTimestamp');

    yield put(AuthActions.authLogoutSuccess(response.data));
  } catch (e: unknown) {
    yield put(
      AuthActions.authLogoutFailure({
        error: { error: e instanceof Error ? e.message : 'Unknown error' },
      }),
    );
  }
}

export function* authSaga() {
  yield all([
    takeLatest(AuthAction.REQUEST_LOGIN_LINK, authRequestLoginLinkSaga),
    takeLatest(AuthAction.INIT, initAuthSaga),
    takeLeading(AuthAction.REFRESH, refreshAuthSaga),
    takeLatest(AuthAction.LOGOUT, logoutSaga),
  ]);
}
